import { URL_ADAF } from '@elements/tools/routes/routes';
import { trans } from '@elements/translation';
import type { MenuItem, MenuItemGroup, MenuItemLink } from '@global-types/nav/menuItem';
import { NotificationType, type UserNotification } from '@models/notification';
import { useUserProfile } from '@pages/pay/composables/useUserProfile';
import { useScreensizeStore } from '@stores/useScreensizeStore';
import { storeToRefs } from 'pinia';
import { computed, ref, watchEffect } from 'vue';
import {
  ACCOUNT_PROFILE,
  BUYER_MENU,
  BUYER_MENU_PLUS,
  BUYER_MOBILE_MENU,
  SELLER_MENU,
  SELLER_MENU_PLUS,
  SELLER_MOBILE_MENU,
  UNQUALIFIED_MENU,
  UNQUALIFIED_MENU_PLUS,
} from '../config';

type MenuPreference = 'buyer' | 'seller';

const menuPreference = ref<MenuPreference>(
  (localStorage.getItem('menuPreference') as MenuPreference) ?? 'seller',
);

// eslint-disable-next-line import/prefer-default-export
export const useNavbarMenu = () => {
  const menuItems = ref<MenuItem[]>([]);
  const stickyMenuItems = ref<MenuItem[]>([]);
  const currentOpenMenu = ref<string | null>(null);

  const hasSubmenus = (menuItem: MenuItem): boolean => 'submenus' in menuItem && Array.isArray(menuItem.submenus) && menuItem.submenus.length > 0;

  const isDivider = (menuItem: MenuItem): boolean => 'type' in menuItem && menuItem.type === 'divider';

  const isMenuLink = (menuItem: MenuItem): boolean => 'link' in menuItem;

  const isMenuOpen = (name: string): boolean => name === currentOpenMenu.value;

  const isCurrentPage = (link: string | undefined): boolean => window.location.pathname === link;

  const isCurrentCategory = (menuItem: MenuItem): boolean => {
    if (!hasSubmenus(menuItem)) {
      return false;
    }

    return (menuItem as MenuItemGroup).submenus
      .some((subMenu) => subMenu.link === window.location.pathname);
  };

  const notifications = computed<UserNotification>(() => window.USER_NOTIFICATIONS as UserNotification);

  const initCurrentMenu = (): void => {
    const menuItemToOpen = menuItems.value.find((item) => {
      if (!isMenuLink(item)) {
        return false;
      }

      return window.location.href.startsWith((item as MenuItemLink).link);
    });

    if (!menuItemToOpen || isDivider(menuItemToOpen)) {
      return;
    }

    currentOpenMenu.value = menuItemToOpen.name;
  };

  initCurrentMenu();

  const showMenuLink = (menuItem: MenuItemLink, userProfile: string): boolean => {
    if (!menuItem.hideOnBothProfile) {
      return true;
    }

    return userProfile !== ACCOUNT_PROFILE.BOTH;
  };

  const toggleMenu = (name: string): void => {
    currentOpenMenu.value = currentOpenMenu.value === name ? null : name;
  };

  const resetMenu = (): void => {
    currentOpenMenu.value = null;
  };

  const getNotificationsCount = (notification: string | undefined): number => {
    if (
      !notification
      || !Object.values(NotificationType).includes(notification as NotificationType)
    ) {
      return 0;
    }

    return notifications.value[notification as NotificationType];
  };

  const { isMobileOrTablet } = storeToRefs(useScreensizeStore());

  const { hasNoProfile, userProfile, isSellerBoost } = useUserProfile();

  const stripAdaf = (
    items: MenuItem[],
    isBoost: boolean,
  ): MenuItem[] => {
    if (!isBoost) {
      return items;
    }

    return items.map((it) => {
      if (!('submenus' in it) || !Array.isArray((it as MenuItemGroup).submenus)) {
        return it;
      }
      const withSubs = it as MenuItemGroup;
      const submenus = withSubs.submenus.filter((sm: MenuItem) => sm.link !== URL_ADAF);
      return submenus.length === withSubs.submenus.length ? it : { ...withSubs, submenus };
    });
  };

  watchEffect((): void => {
    if (hasNoProfile.value) {
      menuItems.value = isMobileOrTablet.value ? UNQUALIFIED_MENU_PLUS : UNQUALIFIED_MENU;
      stickyMenuItems.value = BUYER_MOBILE_MENU;
      return;
    }

    if (userProfile.value !== ACCOUNT_PROFILE.BOTH) {
      stickyMenuItems.value = userProfile.value === ACCOUNT_PROFILE.BUYER
        ? BUYER_MOBILE_MENU
        : SELLER_MOBILE_MENU;

      if (isMobileOrTablet.value) {
        menuItems.value = userProfile.value === ACCOUNT_PROFILE.BUYER
          ? BUYER_MENU_PLUS
          : stripAdaf(SELLER_MENU_PLUS, isSellerBoost.value);
        return;
      }

      menuItems.value = userProfile.value === ACCOUNT_PROFILE.BUYER ? BUYER_MENU : stripAdaf(SELLER_MENU, isSellerBoost.value);
    }

    if (userProfile.value === ACCOUNT_PROFILE.BOTH) {
      const hasBuyerPreference = menuPreference.value === 'buyer';

      stickyMenuItems.value = hasBuyerPreference ? BUYER_MOBILE_MENU : SELLER_MOBILE_MENU;

      if (!isMobileOrTablet.value) {
        menuItems.value = hasBuyerPreference
          ? BUYER_MENU
          : stripAdaf(SELLER_MENU, isSellerBoost.value);
        return;
      }
      menuItems.value = hasBuyerPreference
        ? BUYER_MENU_PLUS
        : stripAdaf(SELLER_MENU_PLUS, isSellerBoost.value);
    }
  });

  const toggleMenuPreference = (): void => {
    menuPreference.value = menuPreference.value === 'buyer' ? 'seller' : 'buyer';
    localStorage.setItem('menuPreference', menuPreference.value);
  };

  const wordingSwitchButton = computed<string>(() => trans(`user_menu.switch_button.${menuPreference.value}`));

  return {
    menuItems,
    stickyMenuItems,
    currentOpenMenu,
    isMenuOpen,
    isCurrentPage,
    toggleMenu,
    resetMenu,
    notifications,
    isCurrentCategory,
    hasSubmenus,
    isDivider,
    isMenuLink,
    showMenuLink,
    getNotificationsCount,
    toggleMenuPreference,
    menuPreference,
    wordingSwitchButton,
  };
};
